<script setup lang="ts">
//#region import
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { SITE_SETTINGS_FETCH } from '@/store/modules/site-settings/action-types';
import { useTypedStore } from '@/store';
import type { GetSitesResponse } from '@/types/openapi-ts';
import type { UiTableNewField } from '@/types/table';
//#endregion

//#region types
type BettingDomain = GetSitesResponse['payload'][number]['betting_domains'][number];
type ReachabilityStatus = Extract<BettingDomain, { reachability: unknown }>['reachability'];

type BettingDomainTableRow = Pick<BettingDomain, 'country_name' | 'domain'> & {
  reachability?: ReachabilityStatus;
  isSubRow?: boolean;
};
//#endregion

//#region composables
const store = useTypedStore();
const { t } = useI18n();
//#endregion

//#region const
const COLORS_MAP: Record<ReachabilityStatus, string> = {
  available: 'var(--text-positive)',
  unavailable: 'var(--text-negative)',
  unknown: 'var(--text-warning)',
};

const bettingDomainsFields: UiTableNewField<BettingDomainTableRow>[] = [
  {
    name: 'country_name',
    width: '300',
    align: 'left',
    headerAlign: 'left',
    renderValue: (value, row) => (
      row.isSubRow ? `<span class="t:pl-16">${value}</span>` : value
    ),
  },
  {
    name: 'domain',
    minWidth: 'auto',
    align: 'left',
    headerAlign: 'left',
    copyValue: (row) => !row.isSubRow,
  },
  {
    name: 'reachability',
    minWidth: 'auto',
    align: 'left',
    headerAlign: 'left',
    computedValueHtml: (row) => {
      if (!row.reachability) {
        return '';
      }

      const status = normalizeReachability(row.reachability);
      return `<span style="color: ${COLORS_MAP[status]}">${t(`settings.reachability.${status}`)}</span>`;
    },
  },
];
//#endregion

//#region data
const siteId = ref('');
//#endregion

//#region computed
const siteSettings = computed(() => store.getters['siteSettings/getSettings']);
const brands = computed(() => store.getters['misc/brands']);
const countriesNameMap = computed(() => store.getters['misc/countriesNameMap']);
const bettingDomainsTableData = computed(() => {
  const domains = siteId.value
    ? siteSettings.value[siteId.value]?.betting_domains ?? []
    : [];

  return domains.flatMap((domain): BettingDomainTableRow[] => {
    if (!('reachability_per_country' in domain)) {
      return [domain];
    }

    const subRows = Object.entries(domain.reachability_per_country).map(
      ([countryCode, reachability]): BettingDomainTableRow => ({
        country_name: countriesNameMap.value[countryCode] ?? countryCode,
        domain: '',
        reachability,
        isSubRow: true,
      }),
    );

    return [{ ...domain, reachability: undefined }, ...subRows];
  });
});
//#endregion

//#region methods
function normalizeReachability (value?: ReachabilityStatus) {
  switch (value) {
    case 'available':
    case 'unavailable':
    case 'unknown':
      return value;
    default:
      return 'unknown';
  }
}

function handleChangeBrand (id: string) {
  siteId.value = id;
}
//#endregion

//#region hooks
siteId.value = brands.value[0]!.id;
store.dispatch(`siteSettings/${SITE_SETTINGS_FETCH}`);
//#endregion

</script>

<template>
  <div class="sites">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('theHeader.menu.settings.domains') }}
      </template>
    </UiToolbar>
    <BrandsList
      :current-brand="siteId"
      @brand-change="handleChangeBrand"
    />
    <UiTableNew
      v-if="siteSettings[siteId!]?.betting_domains"
      :fields="bettingDomainsFields"
      :data="bettingDomainsTableData"
      is-static
      :disable-sort="true"
      i18n-path="settings"
    />
  </div>
</template>
